import { useTranslations } from "@jugl-web/utils";
import { Transition } from "@headlessui/react";
import { useEffect, useState, useMemo } from "react";
import { ReactComponent as LogoIcon } from "./assets/logo.svg";
import { useCreateWorkspaceLoader } from "./useCreateWorkspaceLoader";

export const CreateWorkspaceLoader = () => {
  const { isLoading, canFinishLoading, cancel } = useCreateWorkspaceLoader();
  const [showLoader, setShowLoader] = useState(false); // New state for delayed appearance

  const { t } = useTranslations();
  const [expanded, setExpanded] = useState(false);
  const [showLoadingBar, setShowLoadingBar] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [textChanging, setTextChanging] = useState(false);

  const currentStepIndex = useMemo(() => {
    if (progress < 25) return 0;
    if (progress < 50) return 1;
    if (progress < 75) return 2;
    return 3;
  }, [progress]);

  useEffect(() => {
    if (currentStepIndex !== currentTextIndex) {
      setTextChanging(true);
      const textTimer = setTimeout(() => {
        setCurrentTextIndex(currentStepIndex);
        setTextChanging(false);
      }, 300);

      return () => clearTimeout(textTimer);
    }
    return undefined;
  }, [currentStepIndex, currentTextIndex]);

  useEffect(() => {
    if (isLoading && !isComplete) {
      setShowLoader(true);
    }
    if (!isLoading) {
      setShowLoader(false);
    }
    return undefined;
  }, [isLoading, isComplete]);

  useEffect(() => {
    if (isLoading && !isComplete) {
      const timer = setTimeout(() => setExpanded(true), 700);
      const loadingTimer = setTimeout(() => setShowLoadingBar(true), 1300);

      return () => {
        clearTimeout(timer);
        clearTimeout(loadingTimer);
      };
    }

    if (!isLoading && !isComplete) {
      setExpanded(false);
      setShowLoadingBar(false);
      setProgress(0);
    }
    return undefined;
  }, [isLoading, isComplete]);

  useEffect(() => {
    if (showLoadingBar && !isComplete) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const increment = 3 + 0.5;
          const next = prev + increment;

          if (!canFinishLoading && next > 90) {
            return 90;
          }

          return next >= 100 ? 100 : next;
        });
      }, 300);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [showLoadingBar, isComplete, canFinishLoading]);

  useEffect(() => {
    if (progress >= 100 && !isComplete) {
      setIsComplete(true);
    }
  }, [progress, isComplete]);

  // Disable scroll when loader is visible
  useEffect(() => {
    if (showLoader) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showLoader]);

  const stepText = [
    t({
      id: "workspace.creating-step-1",
      defaultMessage: "Making something awesome just for you",
    }),
    t({
      id: "workspace.creating-step-2",
      defaultMessage: "Your power tools are spinning up",
    }),
    t({
      id: "workspace.creating-step-3",
      defaultMessage: "Big moves loading… stay sharp",
    }),
    t({
      id: "workspace.creating-step-4",
      defaultMessage: "Just a sec - greatness is assembling",
    }),
  ];
  if (!showLoader) {
    return null;
  }
  return (
    <Transition
      as="div"
      show={isLoading && !isComplete}
      enter="transition-all duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-all duration-700 rounded-full"
      leaveFrom="translate-100 opacity-100"
      leaveTo="translate-0 opacity-0"
      className="fixed inset-0 z-[99999] flex h-screen w-screen items-center justify-center overflow-hidden"
      afterLeave={() => {
        setExpanded(false);
        setShowLoadingBar(false);
        setProgress(0);
        setIsComplete(false);
        setCurrentTextIndex(0);
        cancel();
        const event = new CustomEvent("create-workspace-loading-complete");
        document.dispatchEvent(event);
      }}
    >
      <div
        className={`font-secondary bg-primary flex origin-center items-center justify-center overflow-hidden rounded-full transition-all duration-700 ease-in-out ${
          expanded && !isComplete
            ? "h-screen w-screen rounded-none"
            : "h-[293px] w-[293px]"
        }`}
      >
        <div
          className={`animate-fade-in flex flex-col items-center transition-all duration-700 ${
            showLoadingBar ? "-translate-y-20" : ""
          }`}
        >
          <div className="transition-all duration-500">
            <LogoIcon />
          </div>

          {showLoadingBar && (
            <div className="relative h-[80px] overflow-hidden text-white">
              <Transition
                as="div"
                show={!textChanging}
                enter="transition-all duration-700"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-all duration-700"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="animate-fade-in"
              >
                <div className="flex h-[80px] items-center justify-center overflow-hidden truncate text-center text-[32px] font-medium">
                  {stepText[currentTextIndex]}
                </div>
              </Transition>
            </div>
          )}
        </div>

        {showLoadingBar && (
          <div className="fixed bottom-[100px] h-[6px] w-full max-w-[50%] overflow-hidden rounded-full">
            <div
              className="h-full bg-white transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </Transition>
  );
};
